import { useState, useEffect, useMemo, useCallback } from "react";
import { IUser, ICrewBase, IPosition, ISimpleUser, ICrewBaseAssignment } from "@vmosolutions/oc-interfaces";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import {
  FormikTextField,
  FormikDateTimeField,
  FormikSelectField,
  FormikSwitchField,
  FormikPhoneField,
  FormikToggleGroup,
} from "../../../MUI5FormikComponents/FormikWrappers";
import { Button, Grid, Typography, ButtonGroup, Tooltip, Box } from "@mui/material";
import * as Yup from "yup";
import { emailValidation, phoneNumberValidation } from "../../Validation/validation";
import { getCrewGroups } from "../../../../redux/actions/CrewSchedule/CrewSchedule.Actions";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { CrewBaseAssignmentField } from "./CrewBaseAssignmentField";
import { CrewRuleCategoryField } from "./CrewRuleCategoryField";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { fixPhoneNumToString, fixPhoneNumStringToNum } from "../Users/UsersForm";
import _, { cloneDeep } from "lodash";
import { getFormFieldDiff } from "src/utils";
import { CrewScheduleChecksField } from "./CrewScheduleChecksField";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { UserCard } from "./UserCard";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";
import { FieldError } from "src/components/MUI5FormikComponents/FieldError";
import moment from "moment-timezone";

interface ICrewFormProps {
  users: ISimpleUser[];
  selectedCrew?: IUser | null;
  handleRetrieveCrew: (user: IUser) => void;
  updateCrewList: (user: IUser, mode: "create" | "update" | "delete") => void;
  crewBases: ICrewBase[];
  positions: IPosition[];
  setActiveModal: (activeModal: IActiveModal) => void;
}
interface ICrewGroupOption {
  label: string;
  value: string;
  __isNew__?: boolean;
}

export type CrewFormType = FormikProps<IUser>;
const initialCrewValues: IUser = {
  _id: undefined,
  firstName: "",
  lastName: "",
  email: "",
  phoneCell: undefined,
  sendEmail: true,
  foreignRef: [],
  crewInfo: {
    efbDevices: [],
    _old_id: undefined,
    initialFlightHours: 0,
    licenseId: "",
    sex: true,
    isActive: false,
    birthDate: undefined,
    hireDate: undefined,
    phonePrimary: "",
    phoneSecondary: undefined,
    masterSeniority: undefined,
    positions: [],
    bases: [],
    groups: [],
    phoneCell: undefined,
    ruleCategoryAssignments: [],
    scheduleChecks: [],
  },
  active: true,
  allowInternalSignIn: false,
  employeeId: undefined,
  preferredName: undefined,
  middleName: undefined,
  policies: [],
  isApiUser: false,
  token: "",
  createdBy: "",
  sub: [],
};

const CrewSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string().matches(emailValidation, "Please enter a valid email address").required("Email is required"),
  phoneCell: Yup.string()
    .test("regex", "Please enter a valid cellphone number", (number) => {
      if (!number) return true; // return value if undefined (don't run regex).
      return number.length > 0 && phoneNumberValidation.exec(number) ? true : false;
    })
    .nullable(),
  preferredName: Yup.string().nullable(),
  middleName: Yup.string().nullable(),
  employeeId: Yup.string().nullable(),
  crewInfo: Yup.object().shape({
    initialFlightHours: Yup.number().required("Initial Flight Hours required"),
    licenseId: Yup.string().nullable(),
    sex: Yup.boolean().required("Sex is a required field"),
    isActive: Yup.boolean(),
    birthDate: Yup.number().required("Birthdate is required").nullable(),
    hireDate: Yup.number().nullable(),
    phonePrimary: Yup.string()
      .test("len", "Phone number cannot exceed 40 characters", (val) => !val || val.length < 40)
      .required("Primary Phone number is required")
      .nullable(),
    phoneSecondary: Yup.string()
      .test("len", "Phone number cannot exceed 40 characters", (val) => !val || val.length < 40)
      .nullable(),
    masterSeniority: Yup.number()
      .integer("Master seniority must be an integer")
      .typeError("Master seniority must be an integer")
      .nullable(),
    positions: Yup.array()
      .required("Crew Positions is a required field")
      .min(1, "At least one crew position is required"),
    bases: Yup.array().required().min(1, "At least one base assignment is required"),
    phoneCell: Yup.string()
      .test("regex", "Please enter a valid cellphone number", (number) => {
        if (!number) return true; // return value if undefined (don't run regex).
        return number.length > 0 && phoneNumberValidation.exec(number) ? true : false;
      })
      .nullable(),

    groups: Yup.array()
      .of(Yup.string())
      .test("regex", "Group names cannot contain . or [", (groups) => {
        if (groups === undefined || groups?.length === 0) return true; // return value if undefined (don't run regex).
        for (let i = 0; i <= groups.length; i++) {
          const groupName = groups[i];
          if (typeof groupName === "string" && (groupName.indexOf(".") > 0 || groupName.indexOf("[") > 0)) {
            return false;
          }
        }
        return true;
      }),
  }),
});

export const returnNumber = (value: string): number | null => {
  const numberOnly = parseInt(value?.replace(/\D/g, "")) ?? null;
  return !isNaN(numberOnly) ? numberOnly : null;
};

const isNullOrEmpty = (value: string): boolean => {
  return !value || value === undefined || value === "" || value.length === 0;
};

export const CrewForm = (props: ICrewFormProps): JSX.Element => {
  const { selectedCrew, updateCrewList, crewBases, positions, setActiveModal } = props;
  const dispatch = useDispatch();
  const initialValues = useMemo(() => (selectedCrew ? selectedCrew : initialCrewValues), [selectedCrew]);
  const { cancellablePromise } = useCancellablePromise();
  const [crewGroupOptions, setCrewGroupoptions] = useState<ICrewGroupOption[]>([]);
  const [showCrewBaseModal, setShowCrewBaseModal] = useState<boolean>(false);
  const [showRuleCategoryModal, setShowRuleCategoryModal] = useState<boolean>(false);
  const [showScheduleChecksModal, setShowScheduleChecksModal] = useState<boolean>(false);
  const crewRuleCategories = useSelector((state: IRootState) => state.structures.customerStructure.crewRuleCategories);

  const getAndArrangeGroups = useCallback((): any => {
    cancellablePromise(
      getCrewGroups()
        .then((data: string[]) => {
          const groupOptions = data?.length > 0 ? data.map((name) => ({ label: name, value: name })) : [];
          if (Array.isArray(groupOptions)) {
            setCrewGroupoptions(
              groupOptions.sort((a, b) => {
                return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
              })
            );
          }
        })
        .catch(() => setCrewGroupoptions([]))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => !!getAndArrangeGroups && getAndArrangeGroups(), [getAndArrangeGroups]);

  const handleSubmit = useCallback(
    async (values: IUser, helpers: FormikHelpers<IUser>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      const mode = values._id ? "update" : "create";
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Crew Member...`,
        successMessage: `Crew Member Successfully ${mode === "update" ? "Updated" : "Created"}.`,
        errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Crew Member`,
      };
      const changedValues = getFormFieldDiff(values, initialValues);
      const RuleCatAssignments =
        changedValues?.crewInfo?.ruleCategoryAssignments?.length > 0
          ? changedValues?.crewInfo?.ruleCategoryAssignments
          : [];
      const booleanActiveFlag = (): boolean => {
        return changedValues.crewInfo.isActive === "true" || changedValues.crewInfo.isActive === true;
      };

      const baseAssignments = changedValues?.crewInfo?.bases?.length > 0 ? changedValues?.crewInfo?.bases : [];
      const updatedBaseAssignments = baseAssignments.map((CW: ICrewBaseAssignment) => {
        if (CW?._id?.includes("temp_")) {
          return { ...CW, _id: undefined };
        } else {
          return CW;
        }
      });

      const payload = {
        ...changedValues,
        crewInfo: {
          ...changedValues.crewInfo,
          ruleCategoryAssignments: RuleCatAssignments,
          bases: updatedBaseAssignments,
          isActive: booleanActiveFlag(),
        },
      };

      const response =
        mode === "create"
          ? await AmplifyAPIWrapper.Post<IUser>("/user/crew", payload, notifications)
          : await AmplifyAPIWrapper.Put<IUser>(`/user/crew/${values._id}`, payload, notifications);
      if (response?._id) {
        resetForm({ values });
        setStatus({ success: true });
        updateCrewList(response, mode);
        getAndArrangeGroups();
      } else {
        setStatus({ success: false });
      }
      setSubmitting(false);
    },
    [dispatch, getAndArrangeGroups, initialValues, updateCrewList]
  );
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={CrewSchema}
    >
      {(crewForm: CrewFormType) => {
        const { dirty, isValid, isSubmitting, handleReset } = crewForm;
        const handleCreateCrewGroup = (groupName: string): void => {
          const currentValue: string[] = crewForm?.values?.crewInfo?.groups ?? [];
          if (currentValue?.length > 0) {
            for (let i = 0; i < currentValue.length; i++) {
              if (currentValue[i] === groupName) {
                return;
              }
            }
          }
          crewForm.setFieldValue("crewInfo.groups", [...currentValue, groupName], false);
          crewForm.setFieldTouched("crewInfo.groups", true);
        };
        const recentCB = (): ICrewBaseAssignment | undefined => {
          if (crewForm?.values?.crewInfo?.bases && crewForm?.values?.crewInfo?.bases.length > 0) {
            const sortedBases = crewForm.values.crewInfo?.bases.sort((a, b) =>
              a.assignedStart > b.assignedStart ? -1 : a.assignedStart < b.assignedStart ? 1 : 0
            );
            const firstValidCBA = sortedBases.find((cba: ICrewBaseAssignment) => {
              return moment(cba.assignedStart) < moment();
            });
            return firstValidCBA;
          } else {
            return undefined;
          }
        };
        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {selectedCrew && <UserCard user={selectedCrew} />}
                <input type="hidden" name="email" />
                <input type="hidden" name="active" />
                <input type="hidden" name="firstName" />
                <input type="hidden" name="lastName" />
                <input type="hidden" name="employeeId" />
                <input type="hidden" name="preferredName" />
                <input type="hidden" name="middleName" />
                <input type="hidden" name="crew" />
              </Grid>
              <Grid item xs={6}>
                <Typography>Required fields</Typography>
                <FormikSwitchField
                  label="Crew Is Active"
                  name="crewInfo.isActive"
                  defaultValue={initialCrewValues.crewInfo.isActive}
                />
                <FormikTextField label="Initial Flight Hrs" name="crewInfo.initialFlightHours" type="number" />
                <FormikDateTimeField
                  label="Birth Date"
                  name="crewInfo.birthDate"
                  hideTimeControl={true}
                  isEpoch={true}
                  maxDate={moment().subtract(14, "years").format()}
                />

                <FormikToggleGroup
                  label="Sex"
                  name="crewInfo.sex"
                  allowNull={false}
                  exclusive={true}
                  options={[
                    { label: "Male", value: true },
                    { label: "Female", value: false },
                  ]}
                />

                <FormikPhoneField
                  label="Crew Primary Phone #"
                  name="crewInfo.phonePrimary"
                  noCountryCode={true}
                  noMask={true}
                />

                <FormikSelectField
                  name="crewInfo.positions"
                  label="Crew Positions"
                  options={positions}
                  isMulti={true}
                  getOptionLabel={(position: IPosition) => position.description}
                  getOptionValue={(position: IPosition) => position._id}
                  parse={(positions: IPosition[]) => positions ?? []}
                  isClearable={true}
                  hideSelectedOptions={false}
                />
                <Box
                  className="MuiFormControl-root"
                  sx={{
                    justifyContent: !crewForm.isValid && !recentCB()?.hasOwnProperty("base") ? "space-between" : "end",
                    display: "flex",
                    py: 1,
                  }}
                >
                  {!crewForm.isValid && !recentCB()?.hasOwnProperty("base") && (
                    <Box sx={{ height: "20px" }}>
                      <FieldError meta={{ error: "At least one base assignment is required" }} icon={true} />
                    </Box>
                  )}
                  <Tooltip title="Click to manage Crew Bases">
                    <Button color="primary" variant="contained" onClick={() => setShowCrewBaseModal(true)}>
                      Crew Base:{" "}
                      {recentCB()?.hasOwnProperty("base")
                        ? `${recentCB()?.base.ICAO} (${recentCB()?.oneWayCommuteTime} mins)`
                        : "Select Base"}
                    </Button>
                  </Tooltip>
                </Box>

                <CrewBaseAssignmentField
                  crewBases={crewBases}
                  crewForm={crewForm}
                  setActiveModal={setActiveModal}
                  setShowCrewBaseModal={setShowCrewBaseModal}
                  showCrewBaseModal={showCrewBaseModal}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography>Optional fields</Typography>
                <FormikPhoneField
                  label="Cell/SMS#"
                  name="crewInfo.phoneCell"
                  format={fixPhoneNumToString}
                  parse={fixPhoneNumStringToNum}
                />
                <FormikPhoneField
                  label="Crew Secondary Phone #"
                  name="crewInfo.phoneSecondary"
                  noCountryCode={true}
                  noMask={true}
                />
                <FormikTextField label="Crew License Id" name="crewInfo.licenseId" />
                <FormikDateTimeField
                  label="Hire Date"
                  name="crewInfo.hireDate"
                  isEpoch={true}
                  hideTimeControl={true}
                  maxDate={moment().add(1, "years").format()}
                />
                <FormikTextField label="Master Seniority #" name="crewInfo.masterSeniority" type="number" />
                <FormikSelectField
                  name="crewInfo.groups"
                  label="Groups"
                  options={crewGroupOptions}
                  isMulti={true}
                  format={(groups: string[]) => {
                    return groups?.length > 0 ? groups.map((name) => ({ label: name, value: name })) : [];
                  }}
                  parse={(groupOptions: ICrewGroupOption[]) => {
                    return groupOptions?.length > 0 ? groupOptions.map((option) => option.value) : [];
                  }}
                  isClearable={true}
                  hideSelectedOptions={false}
                  onCreateOption={handleCreateCrewGroup}
                />
                <Box sx={{ justifyContent: "end", display: "flex", py: 1 }}>
                  {crewRuleCategories.length > 0 && (
                    <Tooltip title="Click to manage Rule Categories">
                      <Button color="primary" variant="contained" onClick={() => setShowRuleCategoryModal(true)}>
                        Manage Rule Categories ({crewForm?.values?.crewInfo?.ruleCategoryAssignments?.length})
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                <CrewRuleCategoryField
                  showRuleCategoryModal={showRuleCategoryModal}
                  setShowRuleCategoryModal={setShowRuleCategoryModal}
                  crewForm={crewForm}
                  setActiveModal={setActiveModal}
                />

                <Box sx={{ justifyContent: "end", display: "flex", py: 1 }}>
                  <Tooltip title="Click to manage Schedule Checks">
                    <Button color="primary" variant="contained" onClick={() => setShowScheduleChecksModal(true)}>
                      Manage Schedule Checks ({crewForm?.values?.crewInfo?.scheduleChecks?.length})
                    </Button>
                  </Tooltip>
                </Box>
                <CrewScheduleChecksField
                  showScheduleChecksModal={showScheduleChecksModal}
                  setShowScheduleChecksModal={setShowScheduleChecksModal}
                  crewForm={crewForm}
                  setActiveModal={setActiveModal}
                />
              </Grid>

              <Grid item xs={12}>
                <ButtonGroup variant="contained">
                  <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                    {selectedCrew ? "Update" : "Create"}
                  </Button>
                  <Button
                    type="reset"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
